import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { THROW_ON_TERMS_NOT_ACCEPTED } from '@app/shared/constants/http-context-tokens';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';
import { lastValueFrom } from 'rxjs';
import {
    GetMeQuotasResponse,
    LoginOrganizationSite,
    OrganizationSite,
    UserRoles,
} from 'api/models';
import { StorageService } from '../storage-service/storage.service';
import { Role } from '@app/shared/models/authentication/role';
import { AuthenticationService, UsersService } from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import {
    ApiKeysService,
    UsersService as AddonsUsersService,
} from 'api/addons/services';
import {
    haveFeaturesChanged,
    resetPermissions,
    setCurrentOrganization,
    setOrganizationFeatures,
} from '@app/core/stores/permissionsStore';
import { AuthService } from '@app/core/services/auth.service';
import { AppStore } from '@app/stores/appStore';
import { LoginResponse } from '../../../../../api/addons/models/login-response';

@Injectable({
    providedIn: 'root',
})
export class SessionService {
    private appStore = inject(AppStore);

    public currentSiteRole: WritableSignal<CurrentSiteRole> = signal<CurrentSiteRole>(
        null as any
    );
    public readonly userSessionSignal = this.appStore.userSession;

    public currentOrganizationSiteSignal: WritableSignal<LoginOrganizationSite> =
        signal<LoginOrganizationSite>(null as any);

    // Derived from the current organization site signal; kept for addons consumers
    // (e.g. debug tooling) that read the organization key directly.
    public get currentOrganizationKey(): string {
        return this.currentOrganizationSiteSignal()?._key ?? '';
    }

    private organizationSites: OrganizationSite[] = [];
    public quotas: GetMeQuotasResponse;

    constructor(
        private storageService: StorageService,
        private usersService: UsersService,
        private spinner: NgxSpinnerService,
        private router: Router,
        private authenticationService: AuthenticationService,
        private apiKeyService: ApiKeysService,
        private addonsUsersService: AddonsUsersService,
        private authService: AuthService
    ) {
        const currentSiteRole = this.readStoredCurrentSiteRole();
        if (currentSiteRole) {
            this.currentSiteRole.set(currentSiteRole);
        }
    }

    private readStoredCurrentSiteRole(): CurrentSiteRole | null {
        const storedValue = this.storageService.getItem('currentSiteRole');
        if (!storedValue) return null;

        try {
            const parsedValue = JSON.parse(
                storedValue
            ) as Partial<CurrentSiteRole>;
            if (
                !parsedValue ||
                typeof parsedValue !== 'object' ||
                typeof parsedValue.role !== 'string' ||
                !parsedValue.siteInfo ||
                typeof parsedValue.siteInfo._key !== 'string'
            ) {
                throw new Error('Invalid persisted site role');
            }
            return parsedValue as CurrentSiteRole;
        } catch {
            this.storageService.removeItem('currentSiteRole');
            return null;
        }
    }

    public getMyOrganizationSites() {
        return this.organizationSites;
    }

    /**
     * Human-readable name for a site key, falling back to the key itself for
     * sites the user has no access to. Single source of truth for every place
     * that renders or filters an `organizationSiteKey`.
     */
    public getOrganizationSiteName(organizationSiteKey: string): string {
        if (!organizationSiteKey) {
            return '';
        }

        const site = this.organizationSites.find(
            site => site._key === organizationSiteKey
        );
        return site?.name || organizationSiteKey;
    }

    public get isServiceProvider() {
        return !this.userSessionSignal()?.organizations.length;
    }

    public get isOrganizationAdmin() {
        return this.appStore.isOrganizationAdmin();
    }

    public get isSiteAdmin() {
        return this.appStore.isSiteAdmin();
    }

    public getUserRole(
        site: LoginOrganizationSite | OrganizationSite
    ):
        | UserRoles.Carrier
        | UserRoles.Consignee
        | UserRoles.Consignor
        | UserRoles.RemoteConsignor
        | UserRoles.RemoteConsignee
        | UserRoles.RemoteCarrier
        | undefined {
        if (site.consignee && site.roles?.includes(UserRoles.Consignee)) {
            return UserRoles.Consignee;
        }
        if (site.consignee && site.roles?.includes(UserRoles.RemoteConsignee)) {
            return UserRoles.RemoteConsignee;
        }
        if (site.consignor && site.roles?.includes(UserRoles.Consignor)) {
            return UserRoles.Consignor;
        }
        if (site.consignor && site.roles?.includes(UserRoles.RemoteConsignor)) {
            return UserRoles.RemoteConsignor;
        }
        if (site.carrier && site.roles?.includes(UserRoles.Carrier)) {
            return UserRoles.Carrier;
        }
        if (site.carrier && site.roles?.includes(UserRoles.RemoteCarrier)) {
            return UserRoles.RemoteCarrier;
        }
        return undefined;
    }

    public isConsignee(site: LoginOrganizationSite | OrganizationSite) {
        return (
            site.consignee &&
            (site.roles?.includes(UserRoles.Consignee) ||
                site.roles?.includes(UserRoles.RemoteConsignee))
        );
    }

    public isConsignor(site: LoginOrganizationSite | OrganizationSite) {
        return (
            site.consignor &&
            (site.roles?.includes(UserRoles.Consignor) ||
                site.roles?.includes(UserRoles.RemoteConsignor))
        );
    }

    public isCarrier(site: LoginOrganizationSite | OrganizationSite) {
        return (
            site.carrier &&
            (site.roles?.includes(UserRoles.Carrier) ||
                site.roles?.includes(UserRoles.RemoteCarrier))
        );
    }

    public async getFreshSiteInfos(siteKey: string) {
        const me = await lastValueFrom(this.addonsUsersService.getMeAddons());

        // Update permissions store with organization features
        if (me?.organizations && haveFeaturesChanged(me.organizations)) {
            setOrganizationFeatures(me.organizations);
        }

        const site = me.organizationSites.find(site => site._key === siteKey);

        if (!site) return null;

        return site;
    }

    // user is site admin but nothing else on the site
    public isOnlySiteAdmin(
        currentSite: LoginOrganizationSite | OrganizationSite
    ) {
        let isOnlySiteAdmin = this.userSessionSignal()?.roles.includes(
            UserRoles.SiteAdmin
        );

        if (this.isCarrier(currentSite)) return false;
        if (this.isConsignee(currentSite)) return false;
        if (this.isConsignor(currentSite)) return false;

        return isOnlySiteAdmin;
    }

    /**
     * Removes the session from the localStorage of the browser
     */
    public deleteSession(): void {
        // Clear sessionStorage but keep the management-interface token
        let itemsToKeep: { [key: string]: string } = {};
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);

            if (key && key.includes('management-interface')) {
                itemsToKeep[key] = sessionStorage.getItem(key) as string;
            }
        }
        sessionStorage.clear();
        for (const key in itemsToKeep) {
            sessionStorage.setItem(key, itemsToKeep[key]);
        }

        // Reset permissions store on logout
        resetPermissions();

        // Preserve 'receiveNotifications_' items, language setting, and table column settings
        itemsToKeep = {};
        const notificationPrefix = 'receiveNotifications_';
        const columnDataSuffix = '-column-data-';
        const columnConfigSuffix = '-column-config-';
        const seenAnnouncementKey = 'seenAnnouncementKeys';

        // preserve language setting
        let lng;
        if (this.storageService.itemExists('language_code')) {
            lng = this.storageService.getItemAsObject('language_code');
        }

        // Iterate through all localStorage items and preserve notification and column settings
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (
                key &&
                (key.startsWith(notificationPrefix) ||
                    key.includes(columnDataSuffix) ||
                    key.includes(columnConfigSuffix) ||
                    key.includes(seenAnnouncementKey))
            ) {
                itemsToKeep[key] = localStorage.getItem(key) as string;
            }
        }

        // Clear all localStorage
        localStorage.clear();

        // Restore preserved items
        if (lng) {
            this.storageService.setItem('language_code', lng);
        }
        for (const key in itemsToKeep) {
            localStorage.setItem(key, itemsToKeep[key]);
        }

        // Clear all authentication-related data
        this.storageService.removeItem('sessionId');
        this.storageService.removeItem('currentSiteRole');
        this.storageService.removeItem('returnUrl');

        // Reset internal state
        this.organizationSites = [];
        this.quotas = null as any;

        // Reset all signals
        this.appStore.setUserSession(null);
        this.appStore.setAccessibleOrganizationSites([]);
        this.currentSiteRole.set(null as any);
        this.currentOrganizationSiteSignal.set(null as any);
    }

    public orgSiteTypeFlagToRole(organizationSite: LoginOrganizationSite) {
        if (organizationSite.consignor) {
            return Role.CONSIGNOR;
        } else if (organizationSite.consignee) {
            return Role.CONSIGNEE;
        } else if (organizationSite.carrier) {
            return Role.CARRIER;
        }
        return Role.CONSIGNOR;
    }

    public sessionExists(): boolean {
        return this.userSessionSignal() !== null;
    }

    public async isLoggedIn() {
        try {
            return this.authService.isAuthenticated();
        } catch (error) {
            return false;
        }
    }

    public getSession(): LoginResponse {
        return this.userSessionSignal() as LoginResponse;
    }

    public async loadUserFromApi() {
        // Pending terms must surface as a typed error so callers can tell them
        // apart from a failed login (the interceptor already redirected).
        const context = new HttpContext().set(
            THROW_ON_TERMS_NOT_ACCEPTED,
            true
        );
        try {
            const user = await lastValueFrom(
                this.addonsUsersService.getMeAddons(undefined, context)
            );

            // Sort organization sites: non-remote first, remote last
            if (user?.organizationSites) {
                user.organizationSites = this.sortSitesByRemoteStatus(
                    user.organizationSites
                );
            }

            // Update permissions store with organization features
            if (
                user?.organizations &&
                haveFeaturesChanged(user.organizations)
            ) {
                setOrganizationFeatures(user.organizations);
            }

            if (user) {
                this.organizationSites = await lastValueFrom(
                    this.usersService.getAccessibleUserSites(undefined, context)
                );
                await this.renewApiKey(context);
            }

            this.appStore.setAccessibleOrganizationSites(
                this.organizationSites
            );
            this.appStore.setUserSession(user);

            if (user?.organizationSites?.length > 0) {
                const parsedSiteRole = this.readStoredCurrentSiteRole();
                let currentSite = parsedSiteRole
                    ? user.organizationSites.find(
                          site => site._key === parsedSiteRole.siteInfo._key
                      )!
                    : user.organizationSites[0];

                // Stale localStorage entry references a site that no longer exists — fall back
                if (!currentSite) {
                    currentSite = user.organizationSites[0];
                    this.storageService.removeItem('currentSiteRole');
                }

                this.currentSiteRole.set(
                    (currentSite ? parsedSiteRole : null) as CurrentSiteRole
                );
                this.currentOrganizationSiteSignal.set(currentSite);

                // Set current organization in permissions store (addons)
                if (currentSite?.organizationKey) {
                    setCurrentOrganization(currentSite.organizationKey);
                }
            }
        } catch (error) {
            // Reset permissions on error
            resetPermissions();
            throw error;
        }
    }

    public getSessionId() {
        const currentPath = this.router.url;
        const isOnSelfservice = currentPath.startsWith('/selfservice');

        // For selfservice routes, always use localStorage for permanent sessions
        if (isOnSelfservice) {
            const sessionId = this.storageService.getItem('sessionIdPermanent');
            if (sessionId) {
                return sessionId.replaceAll('"', '');
            }
        }
        return null;
    }

    async getQuotas(
        force: boolean = false
    ): Promise<GetMeQuotasResponse | undefined> {
        if (!force) {
            if (this.quotas) return this.quotas;
        }

        try {
            const quotas = await lastValueFrom(this.usersService.getMeQuotas());
            this.quotas = quotas;
        } catch (error) {}
        return this.quotas;
    }

    /**
     * Checks whether the authenticated user has the given role.
     * @param role
     */
    public authorizedUserHasRole(role: Role): boolean {
        const allSites = this.getSession()?.organizationSites;
        if (allSites?.length > 0) {
            const site = allSites.find(
                org => org._key === this.currentOrganizationSiteKey()
            );

            if (site?.consignor && role === Role.CONSIGNOR) {
                return true;
            }

            if (site?.consignee && role === Role.CONSIGNEE) {
                return true;
            }

            if (site?.carrier && role === Role.CARRIER) {
                return true;
            }
        }

        return false;
    }

    /**
     * change current organization sitekey
     */
    public setCurrentOrganizationSite(organizationSiteKey: string) {
        const site = this.getSession().organizationSites.find(
            org => org._key === organizationSiteKey
        );
        if (site) {
            this.currentOrganizationSiteSignal.set(site);
        }
    }

    public currentOrganizationSiteKey(): string {
        return this.currentOrganizationSiteSignal()?._key ?? '';
    }

    public currentOrganizationSite(): LoginOrganizationSite {
        return this.currentOrganizationSiteSignal();
    }

    public async logout(noRedirect: boolean = false) {
        this.authService.logout(noRedirect);
        this.deleteSession();
    }

    async renewApiKey(context?: HttpContext) {
        if (this.userSessionSignal()) {
            if (
                this.userSessionSignal()?.roles.includes(
                    UserRoles.OrganizationAdmin
                )
            ) {
                const organizationKey =
                    this.getSession()?.organizations[0]?._key ?? '';

                await lastValueFrom(
                    this.apiKeyService.renewApiKey(
                        { organizationKey: organizationKey },
                        context
                    )
                );
            }
        }
    }

    public async logoutAfterPasswordChange() {
        await this.spinner.show();
        this.deleteSession();
        this.authService.logout();
    }

    /**
     * Checks if a site is a remote site (read-only access)
     */
    private isRemoteSite(site: LoginOrganizationSite): boolean {
        return site.roles?.some(
            role =>
                role === UserRoles.RemoteConsignor ||
                role === UserRoles.RemoteConsignee ||
                role === UserRoles.RemoteCarrier
        );
    }

    /**
     * Sorts sites so non-remote sites appear first, remote sites last.
     * Preserves original order within each group.
     */
    private sortSitesByRemoteStatus(
        sites: LoginOrganizationSite[]
    ): LoginOrganizationSite[] {
        return [...sites].sort((a, b) => {
            const aIsRemote = this.isRemoteSite(a);
            const bIsRemote = this.isRemoteSite(b);

            if (aIsRemote === bIsRemote) return 0;
            return aIsRemote ? 1 : -1;
        });
    }

    public hasRegisteredInAddons(): boolean | undefined {
        // Read through getSession(), which is typed with the addons LoginResponse
        // (organizations[].config). The upstream 2.4.5 refactor repointed
        // userSessionSignal at AppStore.userSession, typed with the base
        // LoginResponse from api/models, which has no `config` field.
        return this.getSession()?.organizations[0].config.hasRegistered;
    }

    /**
     * Backend message returned by `GET /me` when the user's organization is
     * not unlocked for c4l-addons. Used to distinguish this specific 403 from
     * other authorization failures (expired token, etc.).
     */
    private static readonly ORGANIZATION_NOT_ALLOWED_MESSAGE =
        'The organization of the user is not allowed to use c4l-addons.';

    /**
     * True when the given error is the `/me` 403 that signals the user's
     * organization is not unlocked for c4l-addons. Checks both the 403 status
     * and the specific error message so unrelated 403s are not misclassified.
     * The error body is an array, e.g.
     * `[{ path: '/api/v2/me', message: 'The organization ... c4l-addons.' }]`.
     */
    public isOrganizationNotAllowedError(error: unknown): boolean {
        if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
            return false;
        }

        const body = error.error;
        const message = Array.isArray(body) ? body[0]?.message : body?.message;

        return message === SessionService.ORGANIZATION_NOT_ALLOWED_MESSAGE;
    }
}
